import { useEffect } from 'react'
import { downloadTableCSV, downloadTableExcel, downloadTableJSON } from '../utils/tableExportUtils'
import { appInsights } from '../../../../_services/AppInsights'
import { EventType } from '../../../../utility/types'

interface UseTableActionsProps {
  messageContent: any
  userId: string
  conversationId: string | undefined
}

export const useTableActions = ({
  messageContent,
  userId,
  conversationId
}: UseTableActionsProps) => {
  useEffect(() => {
    if (!messageContent) return

    // Table copy buttons
    const tableCopyButtons: any = document.getElementsByClassName('table-copy-button') || []
    const tableCopyArray = Array.from(tableCopyButtons)
    tableCopyArray?.forEach((button: any) => {
      const originalHTML = button.innerHTML
      button.onclick = () => {
        const table = button.closest('.table-block')?.querySelector('table')
        if (table) {
          navigator.clipboard.writeText(table.innerText).then(() => {
            appInsights.trackEvent({
              name: EventType.ChatArtifactCopied,
              properties: {
                userId,
                conversationId,
                artifactType: 'table',
                format: 'clipboard',
                timestamp: new Date().toISOString()
              }
            })
            button.innerHTML = 'Copied!'
            setTimeout(() => {
              button.innerHTML = originalHTML
            }, 2000)
          })
        }
      }
    })

    // Table download dropdown toggle
    const tableDownloadToggles: any = document.getElementsByClassName('table-download-toggle') || []
    const tableToggleArray = Array.from(tableDownloadToggles)
    tableToggleArray?.forEach((toggle: any) => {
      toggle.onclick = (e: Event) => {
        e.stopPropagation()
        const dropdown = toggle.closest('.table-download-dropdown')
        // Close all other dropdowns first
        document.querySelectorAll('.table-download-dropdown.open').forEach((d: any) => {
          if (d !== dropdown) d.classList.remove('open')
        })
        dropdown?.classList.toggle('open')
      }
    })

    // Close dropdown on outside click
    const handleOutsideClick = () => {
      document.querySelectorAll('.table-download-dropdown.open').forEach((d: any) => {
        d.classList.remove('open')
      })
    }
    document.addEventListener('click', handleOutsideClick)

    // Table scroll left/right buttons - always visible above the table so users never have to
    // scroll down to the bottom of a tall table just to find the horizontal scrollbar.
    const scrollWrappers: any = document.getElementsByClassName('table-scroll-wrapper') || []
    const scrollWrapperArray = Array.from(scrollWrappers)
    scrollWrapperArray?.forEach((wrapper: any) => {
      const tableBlock = wrapper.closest('.table-block')
      const leftBtn = tableBlock?.querySelector('.table-scroll-left-button')
      const rightBtn = tableBlock?.querySelector('.table-scroll-right-button')
      const controls = tableBlock?.querySelector('.table-scroll-buttons')
      if (!leftBtn || !rightBtn || !controls) return

      const updateButtonStates = () => {
        const canScroll = wrapper.scrollWidth > wrapper.clientWidth + 1
        controls.classList.toggle('is-hidden', !canScroll)
        leftBtn.disabled = wrapper.scrollLeft <= 0
        rightBtn.disabled = wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 1
      }

      leftBtn.onclick = () => wrapper.scrollBy({ left: -200, behavior: 'smooth' })
      rightBtn.onclick = () => wrapper.scrollBy({ left: 200, behavior: 'smooth' })
      wrapper.onscroll = updateButtonStates
      updateButtonStates()
    })

    // Table CSV buttons
    const tableCsvButtons: any = document.getElementsByClassName('table-csv-button') || []
    const tableCsvArray = Array.from(tableCsvButtons)
    tableCsvArray?.forEach((button: any) => {
      button.onclick = () => {
        const table = button.closest('.table-block')?.querySelector('table')
        if (table) {
          downloadTableCSV(table)
          appInsights.trackEvent({
            name: EventType.ChatArtifactDownloaded,
            properties: {
              userId,
              conversationId,
              artifactType: 'table',
              format: 'csv',
              timestamp: new Date().toISOString()
            }
          })
        }
        button.closest('.table-download-dropdown')?.classList.remove('open')
      }
    })

    // Table Excel buttons
    const tableExcelButtons: any = document.getElementsByClassName('table-excel-button') || []
    const tableExcelArray = Array.from(tableExcelButtons)
    tableExcelArray?.forEach((button: any) => {
      button.onclick = () => {
        const table = button.closest('.table-block')?.querySelector('table')
        if (table) {
          downloadTableExcel(table)
          appInsights.trackEvent({
            name: EventType.ChatArtifactDownloaded,
            properties: {
              userId,
              conversationId,
              artifactType: 'table',
              format: 'xlsx',
              timestamp: new Date().toISOString()
            }
          })
        }
        button.closest('.table-download-dropdown')?.classList.remove('open')
      }
    })

    // Table JSON buttons
    const tableJsonButtons: any = document.getElementsByClassName('table-json-button') || []
    const tableJsonArray = Array.from(tableJsonButtons)
    tableJsonArray?.forEach((button: any) => {
      button.onclick = () => {
        const table = button.closest('.table-block')?.querySelector('table')
        if (table) {
          downloadTableJSON(table)
          appInsights.trackEvent({
            name: EventType.ChatArtifactDownloaded,
            properties: {
              userId,
              conversationId,
              artifactType: 'table',
              format: 'json',
              timestamp: new Date().toISOString()
            }
          })
        }
        button.closest('.table-download-dropdown')?.classList.remove('open')
      }
    })

    return () => {
      tableCopyArray.forEach((button: any) => {
        button.onclick = null
      })
      tableToggleArray.forEach((button: any) => {
        button.onclick = null
      })
      scrollWrapperArray.forEach((wrapper: any) => {
        wrapper.onscroll = null
        const tableBlock = wrapper.closest('.table-block')
        const leftBtn = tableBlock?.querySelector('.table-scroll-left-button')
        const rightBtn = tableBlock?.querySelector('.table-scroll-right-button')
        if (leftBtn) leftBtn.onclick = null
        if (rightBtn) rightBtn.onclick = null
      })
      tableCsvArray.forEach((button: any) => {
        button.onclick = null
      })
      tableExcelArray.forEach((button: any) => {
        button.onclick = null
      })
      tableJsonArray.forEach((button: any) => {
        button.onclick = null
      })
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [messageContent])
}
